import React, { useState } from 'react';

type Question = {
    name?: string;
    content?: string;
    image?: string;
    language?: string;
    solutionDescription?: string;
    solutionImage?: string;
    positiveMarks?: number;
    negativeMarks?: number;
    type?: string;
};

type Option = {
    id: number;
    content?: string;
    image?: string;
    is_correct?: boolean;
};

type OptionTranslation = {
    option_id: number;
    content?: string;
    image?: string;
    language: string;
};

type Props = {
    data: {
        question?: Question;
        options?: Option[];
        questionLang?: Question[];
        optionLang?: OptionTranslation[];
    };
};

const QuestionViewer: React.FC<Props> = ({ data }) => {
    const question = data?.question || {};
    const options = data?.options || [];
    const questionLang = data?.questionLang || [];
    const optionLang = data?.optionLang || [];

    const [selectedLang, setSelectedLang] = useState<string>('English');

    const languages = questionLang
        ?.map(q => q?.language || 'English')
        ?.filter((lang, index, self) => self.indexOf(lang) === index) || ['English'];

    function getQuestionByLanguage(lang: string): Question {
        return questionLang?.find(q => q?.language === lang) || question;
    }

    function getOptionsByLanguage(lang: string): Option[] {
        return (
            options?.map(opt => {
                const translation = optionLang?.find(
                    oLang => oLang.language === lang && oLang.option_id === opt.id,
                );
                return {
                    ...opt,
                    content: translation?.content || opt.content,
                    image: translation?.image || opt.image,
                };
            }) || []
        );
    }

    const currentQuestion = getQuestionByLanguage(selectedLang);
    const currentOptions = getOptionsByLanguage(selectedLang);

    return (
        <div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                {languages.map(lang => (
                    <button
                        key={lang}
                        onClick={() => setSelectedLang(lang)}
                        style={{
                            padding: '8px 16px',
                            border: '1px solid #ccc',
                            borderBottom:
                                selectedLang === lang ? '2px solid blue' : '1px solid #ccc',
                            background: selectedLang === lang ? '#e0f0ff' : '#fff',
                            fontWeight: selectedLang === lang ? 'bold' : 'normal',
                            cursor: 'pointer',
                            borderRadius: '6px',
                        }}
                    >
                        {lang}
                    </button>
                ))}
            </div>

            {/* Question Content */}
            <div>
                {currentQuestion?.name && (
                    <div className="flex flex-row gap-2 py-2">
                        <p className="font-bold">Question Name:</p>
                        <h3 dangerouslySetInnerHTML={{ __html: currentQuestion.name }} />
                    </div>
                )}
                {currentQuestion?.content && (
                    <div className="flex flex-col gap-2 py-2">
                        <p className="font-bold">Question Content:</p>
                        <p
                            className="border p-3"
                            dangerouslySetInnerHTML={{ __html: currentQuestion.content }}
                        />
                    </div>
                )}
                {currentQuestion?.image && (
                    <div className="flex flex-col gap-2 py-2">
                        <p className="font-bold">Question Image:</p>
                        <img src={currentQuestion.image} alt="Question" className="w-full" />
                    </div>
                )}
            </div>

            {/* Options */}
            <div className="pt-2 grid grid-cols-2 gap-4">
                {(currentOptions || []).map((opt, idx) => (
                    <div key={opt.id} className="flex flex-row gap-2 items-start">
                        <p className="font-bold">{idx + 1}.</p>
                        <div
                            className={`w-full ${
                                opt?.is_correct ? 'border border-green-500 bg-green-100' : 'border'
                            }`}
                        >
                            <p
                                className="w-full p-2"
                                dangerouslySetInnerHTML={{ __html: opt?.content || '' }}
                            />

                            {opt?.image && (
                                <img
                                    src={opt.image}
                                    className="h-auto max-h-[200px] w-full object-contain mt-2 my-3"
                                    alt=""
                                />
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Marks */}
            {(data?.question?.positiveMarks !== undefined ||
                data?.question?.negativeMarks !== undefined) && (
                <div className="flex gap-4 mt-6 p-3 bg-gray-100 rounded">
                    {data?.question?.positiveMarks !== undefined && (
                        <div>
                            <p className="font-bold text-green-600">
                                Positive Marks: {data?.question?.positiveMarks}
                            </p>
                        </div>
                    )}
                    {data?.question?.negativeMarks !== undefined && (
                        <div>
                            <p className="font-bold text-red-600">
                                Negative Marks: {data?.question?.negativeMarks}
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Solution */}
            {(currentQuestion?.solutionDescription || currentQuestion?.solutionImage) && (
                <div className="mt-6 p-3 bg-blue-50 rounded">
                    <p className="font-bold mb-2">Solution:</p>
                    {currentQuestion?.solutionDescription && (
                        <div className="mb-2">
                            <p
                                className="border p-3 bg-white"
                                dangerouslySetInnerHTML={{
                                    __html: currentQuestion.solutionDescription,
                                }}
                            />
                        </div>
                    )}
                    {currentQuestion?.solutionImage && (
                        <div>
                            <img
                                src={currentQuestion.solutionImage}
                                alt="Solution"
                                className="w-full rounded"
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default QuestionViewer;
